/**
 * The show style in the SPA (D32): the JSON editor's validation with the
 * engine's `showStyleConfigSchema` (the hosts' schema, cross-references
 * included) and the structured view the show-style page shows — its abstract
 * layers and the drivers each allows, source and output layers, piece types
 * with their params and timeline layers, actions and adlib panels.
 */
import { MAX_SHOW_STYLE_CONFIG_CHARS } from '@norna/api-contract/configDocument.js';
import { layerUsers, type ShowStyleConfig, showStyleConfigSchema } from '@norna/playout-engine/showStyle.js';
import { checkDocumentText, type DocumentCheck } from '../studio/config.js';

export type ShowStyleCheck = DocumentCheck<ShowStyleConfig>;

/** Check the show-style editor's text. */
export function checkShowStyleText(text: string): ShowStyleCheck {
  return checkDocumentText(text, showStyleConfigSchema, MAX_SHOW_STYLE_CONFIG_CHARS, 'show style config');
}

export type ShowStyleSummaryView = {
  readonly layers: readonly { readonly id: string; readonly label: string; readonly deviceTypes: readonly string[]; readonly usedBy: readonly string[] }[];
  readonly sourceLayers: readonly { readonly id: string; readonly label: string }[];
  readonly outputLayers: readonly { readonly id: string; readonly label: string; readonly isPgm: boolean }[];
  readonly pieceTypes: readonly {
    readonly id: string;
    readonly label: string;
    readonly sourceLayer: string;
    readonly lifespan: string;
    readonly params: readonly { readonly name: string; readonly type: string; readonly required: boolean }[];
    /** The abstract layers its timeline template targets, each once. */
    readonly layers: readonly string[];
  }[];
  readonly actions: readonly { readonly id: string; readonly label: string; readonly steps: readonly string[] }[];
  readonly adlibPanels: readonly { readonly id: string; readonly label: string; readonly items: readonly string[] }[];
};

export function showStyleSummary(config: ShowStyleConfig): ShowStyleSummaryView {
  const users = layerUsers(config);
  return {
    layers: Object.entries(config.layers).map(([id, l]) => ({ id, label: l.label, deviceTypes: [...l.deviceTypes], usedBy: [...(users.get(id) ?? [])] })),
    sourceLayers: Object.entries(config.sourceLayers).map(([id, l]) => ({ id, label: l.label })),
    outputLayers: Object.entries(config.outputLayers).map(([id, l]) => ({ id, label: l.label, isPgm: l.isPgm })),
    pieceTypes: Object.entries(config.pieceTypes).map(([id, t]) => ({
      id,
      label: t.label,
      sourceLayer: t.sourceLayer,
      lifespan: t.lifespan,
      params: Object.entries(t.params).map(([name, p]) => ({ name, type: p.type, required: p.required })),
      layers: [...new Set(t.timeline.map((e) => e.layer))],
    })),
    actions: Object.entries(config.actions).map(([id, a]) => ({
      id,
      label: a.label,
      steps: a.steps.map((step) => ('stop' in step ? `stop ${step.stop}` : `${step.mode === 'queue' ? 'queue' : 'fire'} ${step.pieceType}`)),
    })),
    adlibPanels: config.adlibPanels.map((p) => ({
      id: p.id,
      label: p.label,
      items: p.items.map((item) => `${item.label}${item.hotkey === null ? '' : ` (${item.hotkey})`}`),
    })),
  };
}
